<template>
  <main>
    <section class="hero">
      <div class="hero-content">
        <h2>For Students</h2>
        <p>Free curriculum, community, and resources to help you build real-world tech skills</p>
      </div>
    </section>

    <section class="content-section">
      <div class="container">
        <h2>Student Resources</h2>
        <p>Pick a resource to learn more about what's available to you.</p>

        <div class="resource-grid">
          <router-link
            v-for="resource in resources"
            :key="resource.title"
            :to="resource.to"
            class="resource-card"
          >
            <div class="resource-icon">
              <img
                v-if="resource.image"
                :src="resource.image"
                :alt="`${resource.title} logo`"
              >
              <span v-else>{{ resource.emoji }}</span>
            </div>
            <h3>{{ resource.title }}</h3>
            <p>{{ resource.description }}</p>
            <span class="resource-link">Learn more &rarr;</span>
          </router-link>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup lang="ts">
const resources = [
  {
    title: 'Courses',
    emoji: '💻',
    image: '',
    description: 'Free, self-paced curriculum developed by Space Coast STEM. Coding, computer fundamentals, and more.',
    to: '/students/courses'
  },
  {
    title: 'Mattermost',
    emoji: '💬',
    image: '/assets/img/logos/mattermost.png',
    description: 'Our private, safe messaging platform where students can connect with volunteer mentors for help and collaboration.',
    to: '/students/mattermost'
  }
]
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap');

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.hero {
  background: linear-gradient(135deg, var(--primary-color, #2c4a6b) 0%, var(--secondary-color, #3ba3d1) 100%);
  color: var(--white, #ffffff);
  padding: 8rem 2rem;
  text-align: center;
}

.hero-content h2 {
  font-size: 3rem;
  margin-bottom: 1rem;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
}

.hero-content p {
  font-size: 1.3rem;
  opacity: 0.95;
}

.content-section {
  padding: 5rem 2rem;
}

.content-section h2 {
  font-size: 2.5rem;
  margin-bottom: 2rem;
  color: var(--primary-color, #2c4a6b);
  font-family: 'Poppins', sans-serif;
}

.content-section p {
  font-size: 1.1rem;
  line-height: 1.8;
  color: var(--text-light, #666666);
  margin-bottom: 1rem;
}

.resource-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
}

.resource-card {
  background: var(--white, #ffffff);
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s, box-shadow 0.3s;
  text-decoration: none;
  color: inherit;
  display: flex;
  flex-direction: column;
}

.resource-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.resource-icon {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: 70px;
  margin-bottom: 1rem;
}

.resource-icon img {
  max-height: 70px;
  max-width: 70px;
  object-fit: contain;
}

.resource-icon span {
  font-size: 3rem;
}

.resource-card h3 {
  color: var(--primary-color, #2c4a6b);
  margin-bottom: 1rem;
  margin-top: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 1.4rem;
}

.resource-card p {
  color: var(--text-light, #666666);
  line-height: 1.6;
  flex-grow: 1;
  margin-bottom: 1.25rem;
}

.resource-link {
  color: var(--secondary-color, #3ba3d1);
  font-weight: 600;
}

@media (max-width: 768px) {
  .hero-content h2 {
    font-size: 2rem;
  }

  .hero {
    padding: 4rem 2rem;
  }

  .content-section h2 {
    font-size: 2rem;
  }

  .resource-grid {
    grid-template-columns: 1fr;
  }
}
</style>
